import React, { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  ATTRIBUTES, POSITIONS, SIZES, computeOverall, defaultAttributes,
  positionAbbrev, formatDateDE,
} from "@/lib/playerConstants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import StarRating from "@/components/StarRating";
import { ChevronLeft, ChevronRight, Save, ArrowLeft, Plus, Minus } from "lucide-react";
import { toast } from "sonner";

const STEPS = [
  { n: 1, label: "Person" },
  { n: 2, label: "Attribute & Talent" },
];

function PillGroup({ value, onChange, options, formatter, testIdPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            onClick={() => onChange(opt)}
            className={
              "min-h-[44px] px-4 rounded-md border text-sm font-medium transition flex-1 sm:flex-none sm:min-w-[64px] " +
              (active
                ? "bg-brand border-brand text-white shadow-sm"
                : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-brand-lighter/60 hover:text-zinc-100")
            }
            data-testid={`${testIdPrefix}-${opt}`}
          >
            {formatter ? formatter(opt) : opt}
          </button>
        );
      })}
    </div>
  );
}

function NumberCounter({ value, onChange, min = 1, max = 99, testIdPrefix = "counter" }) {
  const dec = () => onChange(Math.max(min, value - 1));
  const inc = () => onChange(Math.min(max, value + 1));
  return (
    <div className="flex items-center gap-3" data-testid={`${testIdPrefix}-counter`}>
      <button
        type="button"
        onClick={dec}
        className="h-12 w-12 rounded-md bg-zinc-800 hover:bg-zinc-700 active:bg-zinc-600 text-zinc-100 flex items-center justify-center transition disabled:opacity-40"
        disabled={value <= min}
        aria-label="Minus"
        data-testid={`${testIdPrefix}-minus`}
      >
        <Minus className="h-5 w-5" />
      </button>
      <div className="flex-1 sm:flex-none sm:min-w-[88px] text-center font-display text-3xl font-semibold tabular-nums text-zinc-100 bg-zinc-950 border border-zinc-800 rounded-md h-12 flex items-center justify-center px-4" data-testid={`${testIdPrefix}-value`}>
        {value}
      </div>
      <button
        type="button"
        onClick={inc}
        className="h-12 w-12 rounded-md bg-brand hover:bg-brand-light active:bg-brand-dark text-white flex items-center justify-center transition disabled:opacity-40"
        disabled={value >= max}
        aria-label="Plus"
        data-testid={`${testIdPrefix}-plus`}
      >
        <Plus className="h-5 w-5" />
      </button>
    </div>
  );
}

export default function PlayerWizard() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const location = useLocation();

  const initialStep = location.state?.step === 2 ? 2 : 1;
  const [step, setStep] = useState(initialStep);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [form, setForm] = useState({
    name: "",
    geburtsdatum: "",
    koerpergroesse: 160,
    position: "Mittelfeld",
    trikotnummer: 10,
    trikotgroesse: "M",
    hosengroesse: "M",
    attribute: defaultAttributes(),
    talent: 3,
  });

  useEffect(() => {
    if (!isEdit) return;
    (async () => {
      try {
        const { data } = await api.get(`/players/${id}`);
        setForm({
          name: data.name,
          geburtsdatum: data.geburtsdatum,
          koerpergroesse: data.koerpergroesse,
          position: data.position,
          trikotnummer: data.trikotnummer,
          trikotgroesse: data.trikotgroesse,
          hosengroesse: data.hosengroesse,
          attribute: data.attribute,
          talent: data.talent,
        });
      } catch {
        toast.error("Spieler nicht gefunden");
        navigate("/dashboard");
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isEdit, navigate]);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const setAttr = (k, v) => setForm((f) => ({ ...f, attribute: { ...f.attribute, [k]: v } }));

  const overall = computeOverall(form.attribute);

  const canContinueFromStep1 =
    form.name.trim().length > 0 &&
    /^\d{4}-\d{2}-\d{2}$/.test(form.geburtsdatum) &&
    Number(form.koerpergroesse) >= 80;

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        koerpergroesse: Number(form.koerpergroesse),
        trikotnummer: Number(form.trikotnummer),
        talent: Number(form.talent),
      };
      if (isEdit) {
        await api.put(`/players/${id}`, payload);
        toast.success("Spieler aktualisiert");
      } else {
        await api.post("/players", payload);
        toast.success("Spieler angelegt");
      }
      navigate("/dashboard");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div className="text-center py-20 text-zinc-500 font-display tracking-wide">Lädt…</div>;
  }

  return (
    <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
      <button
        onClick={() => navigate("/dashboard")}
        className="text-xs text-zinc-400 hover:text-brand-lighter transition flex items-center gap-1 mb-5"
        data-testid="wizard-back-btn"
      >
        <ArrowLeft className="h-3 w-3" /> Zurück zum Dashboard
      </button>

      {/* Header + progress */}
      <div className="mb-6">
        <div className="text-[11px] uppercase tracking-wider text-brand-lighter mb-1">
          {isEdit ? "Spieler bearbeiten" : "Neuer Spieler"}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-zinc-100">
          {STEPS[step - 1].label}
        </h1>
      </div>

      <div className="flex items-center gap-2 mb-6">
        {STEPS.map((s, idx) => (
          <React.Fragment key={s.n}>
            <button
              onClick={() => s.n < step && setStep(s.n)}
              disabled={s.n > step}
              className={
                "flex items-center gap-2 text-xs font-medium tracking-wide transition " +
                (s.n === step
                  ? "text-brand-lighter"
                  : s.n < step
                    ? "text-zinc-300 hover:text-brand-lighter cursor-pointer"
                    : "text-zinc-600")
              }
            >
              <span className={
                "h-6 w-6 rounded-full flex items-center justify-center text-[11px] font-semibold border " +
                (s.n <= step ? "bg-brand border-brand text-white" : "bg-transparent border-zinc-700 text-zinc-500")
              }>
                {s.n.toString().padStart(2, "0")}
              </span>
              <span className="uppercase">{s.label}</span>
            </button>
            {idx === 0 && <div className={"flex-1 h-px " + (step > 1 ? "bg-brand" : "bg-zinc-800")} />}
          </React.Fragment>
        ))}
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.18 }}
          className="rounded-lg border border-zinc-800 bg-zinc-900/40 p-5 sm:p-6"
        >
          {step === 1 && (
            <div className="space-y-5" data-testid="wizard-step-1">
              <FieldRow label="Name">
                <Input
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
                  placeholder="Max Mustermann"
                  data-testid="input-name"
                />
              </FieldRow>

              <FieldRow label="Geburtsdatum" hint={form.geburtsdatum ? `Jahrgang ${form.geburtsdatum.slice(0, 4)} · ${formatDateDE(form.geburtsdatum)}` : null}>
                <Input
                  type="date"
                  value={form.geburtsdatum}
                  onChange={(e) => setField("geburtsdatum", e.target.value)}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
                  data-testid="input-geburtsdatum"
                />
              </FieldRow>

              <FieldRow label="Körpergröße" suffix="cm">
                <Input
                  type="number"
                  min={80}
                  max={230}
                  value={form.koerpergroesse}
                  onChange={(e) => setField("koerpergroesse", e.target.value)}
                  className="bg-zinc-950 border-zinc-800 text-zinc-100 focus-visible:ring-brand h-11 rounded-md"
                  data-testid="input-koerpergroesse"
                />
              </FieldRow>

              <FieldRow label="Position">
                <PillGroup
                  value={form.position}
                  onChange={(v) => setField("position", v)}
                  options={POSITIONS}
                  formatter={(p) => `${positionAbbrev(p)} · ${p}`}
                  testIdPrefix="pos"
                />
              </FieldRow>

              <FieldRow label="Lieblings-Trikotnummer">
                <NumberCounter
                  value={Number(form.trikotnummer)}
                  onChange={(v) => setField("trikotnummer", v)}
                  min={1}
                  max={99}
                  testIdPrefix="trikotnr"
                />
              </FieldRow>

              <FieldRow label="Trikotgröße">
                <PillGroup
                  value={form.trikotgroesse}
                  onChange={(v) => setField("trikotgroesse", v)}
                  options={SIZES}
                  testIdPrefix="trikot"
                />
              </FieldRow>

              <FieldRow label="Hosengröße">
                <PillGroup
                  value={form.hosengroesse}
                  onChange={(v) => setField("hosengroesse", v)}
                  options={SIZES}
                  testIdPrefix="hose"
                />
              </FieldRow>
            </div>
          )}

          {step === 2 && (
            <div data-testid="wizard-step-2">
              <div className="flex items-center justify-between mb-5 pb-4 border-b border-zinc-800">
                <div>
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400">Live Gesamtbewertung</div>
                  <div className="font-display text-4xl font-bold text-brand-lighter tabular-nums leading-none mt-1" data-testid="wizard-overall">
                    {overall.toFixed(1)}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-1.5">Talent</div>
                  <StarRating
                    value={form.talent}
                    onChange={(v) => setField("talent", v)}
                    testIdPrefix="talent-star"
                    size={24}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-4">
                {ATTRIBUTES.map((a) => (
                  <div key={a.key}>
                    <div className="flex items-center justify-between mb-1.5">
                      <Label className="text-sm text-zinc-200">{a.label}</Label>
                      <span className="font-display text-base font-semibold text-brand-lighter tabular-nums" data-testid={`attr-value-${a.key}`}>
                        {form.attribute[a.key]}
                      </span>
                    </div>
                    <Slider
                      min={1}
                      max={10}
                      step={1}
                      value={[form.attribute[a.key]]}
                      onValueChange={(v) => setAttr(a.key, v[0])}
                      className="[&_[role=slider]]:bg-brand [&_[role=slider]]:border-brand [&>span:first-child]:bg-zinc-800 [&>span:first-child>span]:bg-brand-lighter"
                      data-testid={`slider-${a.key}`}
                    />
                  </div>
                ))}
              </div>

              {/* Compact summary */}
              <div className="mt-6 pt-5 border-t border-zinc-800">
                <div className="text-[11px] uppercase tracking-wider text-zinc-400 mb-3">Zusammenfassung</div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 text-sm">
                  <SummaryItem label="Name" value={form.name} />
                  <SummaryItem label="Geburtstag" value={formatDateDE(form.geburtsdatum)} />
                  <SummaryItem label="Position" value={positionAbbrev(form.position)} />
                  <SummaryItem label="Größe" value={form.koerpergroesse ? `${form.koerpergroesse} cm` : "—"} />
                  <SummaryItem label="Trikot-Nr." value={form.trikotnummer} />
                  <SummaryItem label="Trikot/Hose" value={`${form.trikotgroesse} / ${form.hosengroesse}`} />
                </div>
              </div>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* Nav */}
      <div className="flex justify-between gap-3 mt-5">
        <Button
          variant="outline"
          disabled={step === 1}
          onClick={() => setStep(step - 1)}
          className="bg-transparent border-zinc-700 text-zinc-200 hover:bg-zinc-900 hover:text-brand-lighter rounded-md h-11 flex-1 sm:flex-none"
          data-testid="wizard-prev-btn"
        >
          <ChevronLeft className="h-4 w-4 mr-1" /> Zurück
        </Button>
        {step < 2 ? (
          <Button
            disabled={!canContinueFromStep1}
            onClick={() => setStep(step + 1)}
            className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11 flex-1 sm:flex-none sm:min-w-[180px]"
            data-testid="wizard-next-btn"
          >
            Weiter <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        ) : (
          <Button
            onClick={handleSave}
            disabled={saving}
            className="bg-brand hover:bg-brand-light text-white font-semibold rounded-md h-11 flex-1 sm:flex-none sm:min-w-[180px]"
            data-testid="wizard-save-btn"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? "Speichern…" : isEdit ? "Aktualisieren" : "Spieler speichern"}
          </Button>
        )}
      </div>
    </div>
  );
}

function FieldRow({ label, hint, suffix, children }) {
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium text-zinc-300">{label}{suffix && <span className="text-zinc-500 font-normal ml-1">({suffix})</span>}</Label>
        {hint && <span className="text-[11px] text-brand-lighter">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

function SummaryItem({ label, value }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-wide text-zinc-500">{label}</div>
      <div className="text-zinc-100 font-medium mt-0.5 text-sm">{value || "—"}</div>
    </div>
  );
}
